import type {
  BorderProps,
  ColorProps,
  LayoutProps,
  SpacingProps,
  TypographyProps,
  ResponsiveValue,
} from '@shopify/restyle';
import { border, color, createRestyleComponent, spacing, typography, useTheme } from '@shopify/restyle';

import type { ReactElement, ReactNode, RefObject } from 'react';
import { useState } from 'react';
import type { FieldError } from 'react-hook-form';
import type { ColorValue, LayoutChangeEvent, TextInputProps, TextStyle } from 'react-native';
import { Platform, Pressable, TextInput, View } from 'react-native';
import { Column, Row } from '../box';
import { FormError } from '../form-error';
import { Text } from '../text';
import { useTranslation } from 'react-i18next';
import { env } from 'app/lib/env';
import { Button } from '../button';
import type { Theme } from 'app/shopify';
import { IconEye, IconEyeOff } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH, isMobileNative } from 'app/utils/constants';

type RestyleProps = BorderProps<Theme> &
  ColorProps<Theme> &
  LayoutProps<Theme> &
  SpacingProps<Theme> &
  TypographyProps<Theme>;

const BaseInput = createRestyleComponent<RestyleProps & TextInputProps, Theme>(
  [border, color, spacing, typography],
  TextInput,
);

export interface InputProps extends Omit<
  TextInputProps,
  'inlineImageLeft' | 'inlineImageLeftStyle' | 'placeholderTextColor' | 'selectionColor' | 'value'
> {
  error?: FieldError;
  backgroundColor?: ColorValue;
  label?: string;
  labelSuffix?: string | ReactElement | null;
  labelRight?: ReactNode;
  left?: ReactNode;
  markAsRequired?: boolean;
  right?: ReactNode;
  value?: string | null;
  labelColor?: keyof Theme['colors'];
  innerRef?: RefObject<TextInput | null>;
  autoWidth?: boolean;
  onPress?: () => void;
  fieldBorderRadius?: ResponsiveValue<keyof Theme['borderRadii'], Theme['breakpoints']>;
  fieldBorderColor?: ResponsiveValue<keyof Theme['colors'], Theme['breakpoints']>;
  fieldBorderWidth?: number;
  fieldHeight?: number;
  labelFontWeight?: '400' | '700';
}

function InputRight({
  onLayout,
  right,
  secureTextEntry,
  setShowPassword,
  showPassword,
}: {
  onLayout: (event: LayoutChangeEvent) => void;
  right?: ReactNode;
  secureTextEntry?: boolean;
  setShowPassword: (showPassword: boolean) => void;
  showPassword: boolean;
}) {
  if (right) return <View onLayout={onLayout}>{right}</View>;
  if (!secureTextEntry) return null;

  return (
    <View onLayout={onLayout}>
      <Pressable onPress={() => setShowPassword(!showPassword)}>
        {showPassword ? <IconEye strokeWidth={ICON_STROKE_WIDTH} /> : <IconEyeOff strokeWidth={ICON_STROKE_WIDTH} />}
      </Pressable>
    </View>
  );
}

function InputLabel({
  labelFontWeight,
  label,
  labelColor,
  labelRight,
  labelSuffix,
  mandatoryText,
  markAsRequired,
}: Pick<InputProps, 'label' | 'labelColor' | 'labelRight' | 'labelSuffix' | 'markAsRequired' | 'labelFontWeight'> & {
  mandatoryText: string;
}) {
  if (!label) return null;

  return (
    <Row alignItems="center" gap="spacing.general.x1">
      <Text color={labelColor ?? 'text.primary'} fontWeight={labelFontWeight} variant="bodyBase">
        {label}
      </Text>
      {markAsRequired ? (
        <Text color={labelColor ?? 'text.primary'} variant="bodyBase">
          ({mandatoryText})*
        </Text>
      ) : null}
      {typeof labelSuffix === 'string' ? (
        <Text color={labelColor ?? 'text.primary'} variant="bodyBase">
          {labelSuffix}
        </Text>
      ) : (
        (labelSuffix ?? null)
      )}
      {labelRight ?? null}
    </Row>
  );
}

export function Input({
  error,
  backgroundColor,
  label,
  labelSuffix,
  labelRight,
  labelColor,
  left,
  markAsRequired,
  right,
  value,
  innerRef,
  autoWidth,
  fieldBorderRadius = 'radius.lg',
  fieldBorderColor = 'border.secondary',
  fieldBorderWidth = 1,
  fieldHeight,
  labelFontWeight = '700',
  secureTextEntry,
  onFocus,
  onBlur,
  onPress,
  style,
  ...rest
}: InputProps) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('common');

  // @TODO This works ok for numbers, but for letters, it won't work well. Need to find a way to calculate the real width based on the value.
  const minWidth =
    theme.spacing['spacing.general.x2'] * 2 +
    theme.spacing['spacing.general.x4'] * 2 +
    (value?.toString().length ?? 0) * 9;

  const [showPassword, setShowPassword] = useState(env.APP_ENV === 'local');
  const [isFocused, setIsFocused] = useState(false);

  const [rightViewWidth, setRightViewWidth] = useState<number>();
  const onRightViewLayout = (event: LayoutChangeEvent) => {
    setRightViewWidth(event.nativeEvent.layout.width);
  };

  const rightReal = (
    <InputRight
      onLayout={onRightViewLayout}
      right={right}
      secureTextEntry={secureTextEntry}
      setShowPassword={setShowPassword}
      showPassword={showPassword}
    />
  );

  const inputRow = (
    <Row
      alignItems="center"
      borderColor={isFocused && !isMobileNative ? 'border.focused' : fieldBorderColor}
      height={fieldHeight}
      borderRadius={fieldBorderRadius}
      borderWidth={fieldBorderWidth}
      gap="spacing.general.x2"
      style={{
        backgroundColor: backgroundColor ?? 'transparent',
        width: '100%',
        paddingLeft:
          theme.spacing['spacing.general.x4'] +
          (rightViewWidth && rest.textAlign === 'center' ? rightViewWidth + theme.spacing['spacing.general.x2'] : 0),
        paddingRight: theme.spacing['spacing.general.x4'],
      }}
    >
      {left}

      <BaseInput
        {...rest}
        onBlur={(event) => {
          setIsFocused(false);
          onBlur?.(event);
        }}
        onFocus={(event) => {
          setIsFocused(true);
          onFocus?.(event);
        }}
        color="text.primary"
        flex={1}
        fontFamily={theme.textVariants.defaults.fontFamily}
        fontSize={theme.textVariants.bodyBase.fontSize}
        fontWeight={theme.textVariants.bodyBase.fontWeight}
        paddingBottom="spacing.general.x2"
        paddingTop="spacing.general.x2"
        placeholderTextColor={theme.colors['text.tertiary']}
        ref={innerRef}
        secureTextEntry={secureTextEntry ? !showPassword : false}
        selectionColor={theme.colors['surface.brand.default']}
        style={[
          {
            pointerEvents: rest.editable === false ? 'none' : 'auto',
            width: '100%',
            outlineStyle: (Platform.OS === 'web' ? 'none' : undefined) as TextStyle['outlineStyle'],
          },
          style,
        ]}
        value={value?.toString() ?? ''}
      />

      {rightReal}
    </Row>
  );

  return (
    <Column gap="spacing.general.x2" {...(autoWidth ? { minWidth } : { width: '100%' })}>
      {label ? (
        <InputLabel
          labelFontWeight={labelFontWeight}
          label={label}
          labelColor={labelColor}
          labelRight={labelRight}
          labelSuffix={labelSuffix}
          mandatoryText={t('mandatory')}
          markAsRequired={markAsRequired}
        />
      ) : null}

      {onPress ? (
        <Button onPress={onPress} variant="plain">
          {inputRow}
        </Button>
      ) : (
        inputRow
      )}

      {error ? <FormError {...{ error }} /> : null}
    </Column>
  );
}
